// src/pages/admin/CompanyProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import {
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Web as WebIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Twitter as TwitterIcon,
} from '@mui/icons-material';

import { fetchCompanyById, updateCompany } from '../../features/companies/companiesSlice';
import { getUserProfile, updatePassword } from '../../features/auth/authSlice';

const CompanyProfilePage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { currentCompany, isLoading, error } = useSelector((state) => state.companies);
  
  // États locaux pour le formulaire de profil
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    licenseNumber: '',
    website: '',
    facebook: '',
    instagram: '',
    twitter: '',
    logo: null,
  });
  
  // États locaux pour le formulaire de changement de mot de passe
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  // État pour la notification
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // État pour les erreurs de validation du formulaire de mot de passe
  const [passwordErrors, setPasswordErrors] = useState({});
  
  // Prévisualisation de l'image
  const [logoPreview, setLogoPreview] = useState('');
  
  // Charger les données de la compagnie
  useEffect(() => {
    if (user && user.company) {
      dispatch(fetchCompanyById(user.company));
    }
  }, [dispatch, user]);
  
  // Mettre à jour le formulaire lorsque les données de la compagnie sont chargées
  useEffect(() => {
    if (currentCompany) {
      setFormData({
        name: currentCompany.name || '',
        email: currentCompany.email || '',
        phone: currentCompany.phone || '',
        address: currentCompany.address || '',
        description: currentCompany.description || '',
        licenseNumber: currentCompany.licenseNumber || '',
        website: currentCompany.website || '',
        facebook: currentCompany.socialMedia?.facebook || '',
        instagram: currentCompany.socialMedia?.instagram || '',
        twitter: currentCompany.socialMedia?.twitter || '',
        logo: null,
      });
      
      if (currentCompany.logo) {
        setLogoPreview(currentCompany.logo.startsWith('http') 
          ? currentCompany.logo 
          : `${process.env.REACT_APP_API_URL || ''}${currentCompany.logo}`);
      }
    }
  }, [currentCompany]);
  
  // Gestionnaire de changement pour le formulaire de profil
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  
  // Gestionnaire de changement pour le formulaire de mot de passe
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };
  
  // Gestionnaire pour l'upload du logo
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Mettre à jour l'état avec le fichier
      setFormData((prev) => ({ ...prev, logo: file }));
      
      // Créer une URL pour prévisualiser l'image
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };
  
  // Valider le formulaire de mot de passe
  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Le mot de passe actuel est requis';
    }
    
    if (!passwordData.newPassword) {
      errors.newPassword = 'Le nouveau mot de passe est requis';
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Soumettre le formulaire de profil
  const handleProfileSubmit = (e) => {
    e.preventDefault();
    
    // Préparer les données pour l'API
    const companyData = {
      ...formData,
      socialMedia: {
        facebook: formData.facebook,
        instagram: formData.instagram,
        twitter: formData.twitter,
      }
    };
    
    // Supprimer les propriétés qui ont été déplacées dans socialMedia
    delete companyData.facebook;
    delete companyData.instagram;
    delete companyData.twitter;
    
    // Mettre à jour la compagnie
    dispatch(updateCompany({ id: currentCompany._id, ...companyData }))
      .unwrap()
      .then(() => {
        setNotification({
          open: true,
          message: 'Profil mis à jour avec succès',
          severity: 'success',
        });
      })
      .catch((err) => {
        setNotification({
          open: true,
          message: `Erreur: ${err}`,
          severity: 'error',
        });
      });
  };
  
  // Soumettre le formulaire de mot de passe
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    
    if (validatePasswordForm()) {
      dispatch(updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      }))
        .unwrap()
        .then(() => {
          setNotification({
            open: true,
            message: 'Mot de passe mis à jour avec succès',
            severity: 'success',
          });
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
        })
        .catch((err) => {
          setNotification({
            open: true,
            message: `Erreur: ${err}`,
            severity: 'error',
          });
        });
    }
  };
  
  // Fermer la notification
  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Profil de votre compagnie
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Informations de la compagnie */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Informations générales
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box component="form" onSubmit={handleProfileSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nom de la compagnie"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Téléphone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Adresse"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={4}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Numéro de licence"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Site web"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WebIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Réseaux sociaux
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Facebook"
                    name="facebook"
                    value={formData.facebook}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FacebookIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Instagram"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <InstagramIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Twitter"
                    name="twitter"
                    value={formData.twitter}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TwitterIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    sx={{ mt: 2 }}
                  >
                    Enregistrer
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
          
          {/* Formulaire de changement de mot de passe */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Changer de mot de passe
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box component="form" onSubmit={handlePasswordSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Mot de passe actuel"
                    name="currentPassword"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    error={Boolean(passwordErrors.currentPassword)}
                    helperText={passwordErrors.currentPassword}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nouveau mot de passe"
                    name="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    error={Boolean(passwordErrors.newPassword)}
                    helperText={passwordErrors.newPassword}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Confirmer le nouveau mot de passe"
                    name="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    error={Boolean(passwordErrors.confirmPassword)}
                    helperText={passwordErrors.confirmPassword}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                  >
                    Mettre à jour le mot de passe
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
        
        {/* Logo et prévisualisation */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Logo de la compagnie
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Box sx={{ mb: 2 }}>
              {logoPreview ? (
                <Avatar
                  src={logoPreview}
                  alt="Logo de la compagnie"
                  sx={{
                    width: 200,
                    height: 200,
                    mx: 'auto',
                    mb: 2,
                    border: '1px solid #eee',
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 200,
                    height: 200,
                    mx: 'auto',
                    mb: 2,
                    fontSize: 64,
                    bgcolor: 'primary.main',
                  }}
                >
                  {formData.name?.charAt(0) || '?'}
                </Avatar>
              )}
              
              <input
                accept="image/*"
                id="logo-upload"
                type="file"
                hidden
                onChange={handleLogoChange}
              />
              <label htmlFor="logo-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<PhotoCameraIcon />}
                >
                  Changer le logo
                </Button>
              </label>
            </Box>
            
            <Typography variant="body2" color="text.secondary">
              Formats acceptés: JPG, PNG, GIF. Taille max: 5 MB.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyProfilePage;